/**

 * Global challenge invites: SSE notifications, incoming dialog, header bell.

 */

import { Alpine } from '../core/alpine-core.js';

import { playNotificationPing } from '../core/notification-sound.js';

import { apiFetch, getAccessToken } from './api.js';

import { venuePersonaAvatarDataUri } from '../public/venue-kiosk-avatars.js';



const DISMISSED_KEY = 'dm.admin.notifications.dismissed';

const SOUND_KEY = 'dm.admin.notifications.sound';



function playerDisplayName(player) {

  if (!player) return 'Player';

  const full = String(player.fullName || 'Player').trim();

  const nick = String(player.nickname || '').trim();

  if (player.isGuest) return full || 'Guest';

  return nick ? `${full} (${nick})` : full;

}



function playerAvatarUrl(player) {

  const url = String(player?.imageUrl || '').trim();

  if (url) return url;

  const seed = String(player?._id || player?.fullName || 'player');

  return venuePersonaAvatarDataUri(seed);

}



function challengeMatchHref(challenge) {

  const id = String(challenge?._id || '');

  if (!id) return '/admin/challenges';

  const mode = String(challenge?.mode || 'local');

  const target = mode === 'local' ? 'scorer' : 'score';

  return `/admin/challenges/${encodeURIComponent(id)}/${target}`;

}



function challengeListHref() {

  return '/admin/challenges';

}



function dismissKey(item) {

  return `${String(item?.kind || 'unknown')}:${String(item?.challengeId || item?.challenge?._id || '')}`;

}



function readDismissedKeys() {

  try {

    const raw = JSON.parse(globalThis.localStorage?.getItem(DISMISSED_KEY) || '[]');

    return Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];

  } catch {

    return [];

  }

}



function writeDismissedKeys(keys) {

  try {

    globalThis.localStorage?.setItem(DISMISSED_KEY, JSON.stringify([...new Set(keys.map(String))]));

  } catch {

    /* ignore */

  }

}



function migrateSoundPreferenceStorage() {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return;
    const raw = storage.getItem(SOUND_KEY);
    if (raw === null) return;
    if (raw === '0' || raw === 'false') {
      storage.setItem(SOUND_KEY, JSON.stringify(false));
      return;
    }
    if (raw === '1' || raw === 'true') {
      storage.setItem(SOUND_KEY, JSON.stringify(true));
    }
  } catch {
    /* ignore */
  }
}

function formatRelativeTime(iso) {

  const ts = Date.parse(String(iso || ''));

  if (!Number.isFinite(ts)) return '';

  const diffSec = Math.round((Date.now() - ts) / 1000);

  if (diffSec < 45) return 'Just now';

  if (diffSec < 3600) return `${Math.max(1, Math.round(diffSec / 60))}m ago`;

  if (diffSec < 86400) return `${Math.max(1, Math.round(diffSec / 3600))}h ago`;

  return `${Math.max(1, Math.round(diffSec / 86400))}d ago`;

}



function challengeFormatHint(challenge) {

  const title = String(challenge?.title || challenge?.name || '').trim();

  const mode = String(challenge?.mode || 'local').trim();

  const game = String(challenge?.format?.game || challenge?.format?.type || '501').trim();

  const bits = [];

  if (title && title !== 'Challenge') bits.push(title);

  bits.push(game || '501');

  bits.push(mode === 'online' ? 'Online' : 'Local');

  return bits.join(' · ');

}



const KIND_META = {

  challenge_incoming: {

    label: 'Challenge',

    icon: 'fa-solid fa-hand-fist',

    tone: 'amber',

    action: 'Incoming challenge',

  },

  challenge_outgoing: {

    label: 'Challenge',

    icon: 'fa-solid fa-hourglass-half',

    tone: 'sky',

    action: 'Awaiting response',

  },

  challenge_active: {

    label: 'Challenge',

    icon: 'fa-solid fa-bolt',

    tone: 'emerald',

    action: 'Match ready',

  },

};



export function registerChallengeNotifications() {
  migrateSoundPreferenceStorage();

  Alpine.store('challengeNotifications', {

    items: [],

    summary: { pendingIncoming: 0, activeMine: 0 },

    panelOpen: false,

    incomingOpen: false,

    incomingChallenge: null,

    busy: false,

    busyId: '',

    dismissedKeys: readDismissedKeys(),

    soundEnabled: Alpine.$persist(true).as(SOUND_KEY),

    _eventSource: null,

    _connected: false,



    visibleItems() {

      const hidden = new Set(this.dismissedKeys);

      return this.items.filter((item) => !hidden.has(dismissKey(item)));

    },



    notificationCount() {

      return this.visibleItems().filter((i) => i.kind === 'challenge_incoming').length;

    },



    hasPlayerInbox() {

      return !!Alpine.store('session')?.rosterSelfPlayerId;

    },



    pruneDismissedKeys() {

      const active = new Set(this.items.map((i) => String(i.challengeId || '')));

      this.dismissedKeys = this.dismissedKeys.filter((key) => {

        const id = key.split(':').slice(1).join(':');

        return active.has(id);

      });

      writeDismissedKeys(this.dismissedKeys);

    },



    dismissNotification(item) {

      const key = dismissKey(item);

      if (!key.endsWith(':')) {

        this.dismissedKeys = [...new Set([...this.dismissedKeys, key])];

        writeDismissedKeys(this.dismissedKeys);

      }

    },



    clearAllNotifications() {

      const keys = this.items.map(dismissKey).filter((k) => !k.endsWith(':'));

      this.dismissedKeys = [...new Set([...this.dismissedKeys, ...keys])];

      writeDismissedKeys(this.dismissedKeys);

    },



    toggleSound() {

      this.soundEnabled = !this.soundEnabled;

      if (this.soundEnabled) playNotificationPing();

    },

    isSoundEnabled() {

      return this.soundEnabled !== false;

    },



    playPingIfEnabled() {

      if (this.soundEnabled) playNotificationPing();

    },



    async refresh() {

      if (!this.hasPlayerInbox()) {

        this.items = [];

        this.summary = { pendingIncoming: 0, activeMine: 0 };

        return;

      }

      try {

        const res = await apiFetch('/challenges/notifications', { skipLogoutOn401: true });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok) return;

        this.items = Array.isArray(data.items) ? data.items : [];

        if (data.summary) this.summary = data.summary;

        this.pruneDismissedKeys();

      } catch {

        /* ignore */

      }

    },



    openPanel() {

      this.panelOpen = !this.panelOpen;

      if (this.panelOpen) this.refresh();

    },



    closePanel() {

      this.panelOpen = false;

    },



    showIncoming(challenge) {

      if (!challenge?._id) return;

      this.incomingChallenge = challenge;

      this.incomingOpen = true;

    },



    closeIncoming() {

      if (this.busy) return;

      this.incomingOpen = false;

      this.incomingChallenge = null;

    },



    incomingChallenger() {

      return this.incomingChallenge?.challengerPlayerId || null;

    },



    incomingChallengerName() {

      return playerDisplayName(this.incomingChallenger());

    },



    incomingChallengerAvatar() {

      return playerAvatarUrl(this.incomingChallenger());

    },



    incomingFormatHint() {

      return challengeFormatHint(this.incomingChallenge);

    },



    itemKindMeta(item) {

      return KIND_META[item?.kind] || { label: 'Update', icon: 'fa-solid fa-bell', tone: 'neutral', action: 'Notification' };

    },



    itemKindLabel(item) {

      return this.itemKindMeta(item).label;

    },



    itemKindIcon(item) {

      return this.itemKindMeta(item).icon;

    },



    itemKindTone(item) {

      return this.itemKindMeta(item).tone;

    },



    itemActionLabel(item) {

      return this.itemKindMeta(item).action;

    },



    itemTitle(item) {

      const c = item?.challenge;

      if (item?.kind === 'challenge_incoming') {

        return `${playerDisplayName(c?.challengerPlayerId)} challenged you`;

      }

      if (item?.kind === 'challenge_outgoing') {

        return `Challenge sent to ${playerDisplayName(c?.challengedPlayerId)}`;

      }

      const opp = c?.challengedPlayerId || c?.challengerPlayerId;

      return `Active match vs ${playerDisplayName(opp)}`;

    },



    itemMessage(item) {

      const c = item?.challenge;

      if (item?.kind === 'challenge_incoming') {

        return `Accept to start ${challengeFormatHint(c)}.`;

      }

      if (item?.kind === 'challenge_outgoing') {

        return `${playerDisplayName(c?.challengedPlayerId)} can accept or decline.`;

      }

      return `Continue ${challengeFormatHint(c)}.`;

    },



    itemTimeAgo(item) {

      return formatRelativeTime(item?.at);

    },



    itemAvatar(item) {

      const c = item?.challenge;

      if (item?.kind === 'challenge_incoming') return playerAvatarUrl(c?.challengerPlayerId);

      if (item?.kind === 'challenge_outgoing') return playerAvatarUrl(c?.challengedPlayerId);

      return playerAvatarUrl(c?.challengedPlayerId || c?.challengerPlayerId);

    },



    isItemBusy(item) {

      const id = String(item?.challengeId || item?.challenge?._id || '');

      return this.busy && this.busyId === id;

    },



    goToChallenge(challenge) {

      const href = challengeMatchHref(challenge);

      this.closePanel();

      this.closeIncoming();

      const R = globalThis.PineconeRouter;

      if (R?.navigate) R.navigate(href);

      else globalThis.location.assign(href);

    },



    goToChallengesList() {

      this.closePanel();

      const R = globalThis.PineconeRouter;

      if (R?.navigate) R.navigate(challengeListHref());

      else globalThis.location.assign(challengeListHref());

    },



    async respondToChallenge(challengeId, action) {

      const id = String(challengeId || '');

      if (!id || this.busy) return false;

      this.busy = true;

      this.busyId = id;

      try {

        const res = await apiFetch(`/challenges/${id}/respond`, {

          method: 'POST',

          body: { action },

        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok) throw new Error(data?.message || `Unable to ${action} challenge`);

        await this.refresh();

        document.dispatchEvent(new CustomEvent('challenge:list-refresh'));

        return data.challenge || null;

      } finally {

        this.busy = false;

        this.busyId = '';

      }

    },



    async acceptIncoming() {

      const id = String(this.incomingChallenge?._id || '');

      if (!id) return;

      try {

        const challenge = await this.respondToChallenge(id, 'accept');

        this.incomingOpen = false;

        this.incomingChallenge = null;

        Alpine.store('toastr')?.add?.('success', 'Challenge accepted — you\'re ready to play.', 8000, {

          actionLabel: 'Go to match now',

          actionHref: challengeMatchHref(challenge),

        });

      } catch (e) {

        Alpine.store('toastr')?.error?.(e?.message || 'Unable to accept challenge', 4000);

      }

    },



    async declineIncoming() {

      const id = String(this.incomingChallenge?._id || '');

      if (!id) return;

      try {

        await this.respondToChallenge(id, 'decline');

        this.incomingOpen = false;

        this.incomingChallenge = null;

        Alpine.store('toastr')?.info?.('Challenge declined.', 3000);

      } catch (e) {

        Alpine.store('toastr')?.error?.(e?.message || 'Unable to decline challenge', 4000);

      }

    },



    async acceptFromPanel(item) {

      const id = String(item?.challengeId || item?.challenge?._id || '');

      if (!id) return;

      try {

        const challenge = await this.respondToChallenge(id, 'accept');

        Alpine.store('toastr')?.add?.('success', 'Challenge accepted.', 6000, {

          actionLabel: 'Open match',

          actionHref: challengeMatchHref(challenge || item.challenge),

        });

      } catch (e) {

        Alpine.store('toastr')?.error?.(e?.message || 'Unable to accept challenge', 4000);

      }

    },



    async declineFromPanel(item) {

      const id = String(item?.challengeId || item?.challenge?._id || '');

      if (!id) return;

      try {

        await this.respondToChallenge(id, 'decline');

        Alpine.store('toastr')?.info?.('Challenge declined.', 3000);

      } catch (e) {

        Alpine.store('toastr')?.error?.(e?.message || 'Unable to decline challenge', 4000);

      }

    },



    handleStreamEvent(eventName, payload) {

      const challenge = payload?.challenge;

      if (!challenge) return;

      if (eventName === 'challenge:incoming') {

        this.playPingIfEnabled();

        this.showIncoming(challenge);

        this.refresh();

        return;

      }

      if (eventName === 'challenge:declined') {

        const me = String(Alpine.store('session')?.rosterSelfPlayerId || '');

        const challengerId = String(challenge?.challengerPlayerId?._id || challenge?.challengerPlayerId || '');

        if (me && challengerId === me) {

          this.playPingIfEnabled();

          const opp = playerDisplayName(challenge?.challengedPlayerId);

          Alpine.store('toastr')?.info?.(`${opp} declined your challenge.`, 5000);

        }

        this.refresh();

        document.dispatchEvent(new CustomEvent('challenge:list-refresh'));

        return;

      }

      if (eventName === 'challenge:accepted') {

        const me = String(Alpine.store('session')?.rosterSelfPlayerId || '');

        const challengerId = String(challenge?.challengerPlayerId?._id || challenge?.challengerPlayerId || '');

        const challengedId = String(challenge?.challengedPlayerId?._id || challenge?.challengedPlayerId || '');

        if (me && challengerId === me) {

          this.playPingIfEnabled();

          const opp = playerDisplayName(challenge?.challengedPlayerId);

          Alpine.store('toastr')?.add?.('success', `${opp} accepted your challenge.`, 8000, {

            actionLabel: 'Go to match now',

            actionHref: challengeMatchHref(challenge),

          });

        } else if (me && challengedId === me) {

          Alpine.store('toastr')?.success?.('Challenge accepted — ready to play.', 4000);

        }

        this.refresh();

        document.dispatchEvent(new CustomEvent('challenge:list-refresh'));

      }

    },



    notificationsEventUrl() {

      const token = encodeURIComponent(getAccessToken() || '');

      const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');

      const path = `/api/challenges/notifications/events?access_token=${token}`;

      return apiBase ? `${apiBase}${path}` : path;

    },



    connect() {

      if (this._eventSource || !window.EventSource || !getAccessToken() || !this.hasPlayerInbox()) return;

      try {

        this._eventSource = new EventSource(this.notificationsEventUrl());

        this._eventSource.addEventListener('connected', () => {

          this._connected = true;

        });

        for (const evt of ['challenge:incoming', 'challenge:declined', 'challenge:accepted', 'challenge:outgoing']) {

          this._eventSource.addEventListener(evt, (event) => {

            let payload = {};

            try {

              payload = JSON.parse(event.data || '{}');

            } catch {

              /* ignore */

            }

            this.handleStreamEvent(evt, payload);

          });

        }

        this._eventSource.onerror = () => {

          this._connected = false;

        };

      } catch {

        this._connected = false;

      }

    },



    disconnect() {

      if (this._eventSource) {

        this._eventSource.close();

        this._eventSource = null;

      }

      this._connected = false;

    },



    init() {

      if (!this.hasPlayerInbox()) return;

      this.refresh();

      this.connect();

    },

  });

}


